/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import { setConfig as setHotLoaderConfig } from 'react-hot-loader';
import 'abortcontroller-polyfill/dist/abortcontroller-polyfill-only';
import moment from 'moment';
// eslint-disable-next-line no-restricted-imports
import {
  configure,
  makeApi,
  supersetTheme,
  initFeatureFlags,
  initFeatureFlagOverrides,
} from '@superset-ui/core';
import { merge } from 'lodash';
import setupClient from './setup/setupClient';
import setupColors from './setup/setupColors';
import setupExtensions from './setup/setupExtensions';
import setupFormatters from './setup/setupFormatters';
import setupDashboardComponents from './setup/setupDashboardComponents';
import { restoreOverrides } from './features/featureFlags/featureFlagSessionStorage';
import { User } from './types/bootstrapTypes';
import getBootstrapData from './utils/getBootstrapData';

if (process.env.WEBPACK_MODE === 'development') {
  setHotLoaderConfig({ logLevel: 'debug', trackTailUpdates: false });
}
setupExtensions();

// eslint-disable-next-line import/no-mutable-exports
export const bootstrapData = getBootstrapData();

// Configure translation
if (typeof window !== 'undefined') {
  configure({ languagePack: bootstrapData.common.language_pack });
  moment.locale(bootstrapData.common.locale);
} else {
  configure();
}

// Configure feature flags
initFeatureFlags(bootstrapData.common.feature_flags);
initFeatureFlagOverrides();

// Restore any admin feature flag overrides from sessionStorage
restoreOverrides();

// Setup SupersetClient
setupClient();

setupColors(
  bootstrapData.common.extra_categorical_color_schemes,
  bootstrapData.common.extra_sequential_color_schemes,
);

// Setup number formatters
setupFormatters(
  bootstrapData.common.d3_format,
  bootstrapData.common.d3_time_format,
);

setupDashboardComponents();

export const theme = merge(
  supersetTheme,
  bootstrapData.common.theme_overrides ?? {},
);

const getMe = makeApi<void, User>({
  method: 'GET',
  endpoint: '/api/v1/me/',
});

const checkLoginElseRedirect = () => {
  if (process.env.NODE_ENV !== 'test') {
    getMe().catch(() => {
      // ignore error, SupersetClient will redirect to login on a 401
    });
  }
};

/**
 * When you re-open the window, we check if you are still logged in.
 * If your session expired or you signed out, we'll redirect to login.
 * If you aren't logged in in the first place (!isActive), then we shouldn't do this.
 */
let retry_timeer_id: NodeJS.Timeout;
const RETRY_INTERVAL = [1_800_000, 1_800_000];
function addOnlineHeartBeat(retryInterval: number[]) {
  return setInterval(
    () => {
      checkLoginElseRedirect();
    },
    Math.random() * (retryInterval[1] - retryInterval[0]) + retryInterval[0],
  );
}
// eslint-disable-next-line prefer-destructuring
const location = window.location;
const LOGIN_CONTEXT_PATH = '/login/';
const LOGOUT_SUCCESS_CONTEXT_PATH = '/logout_success/';
// console.log(location);
if (
  !bootstrapData.user?.isActive &&
  location.pathname !== LOGIN_CONTEXT_PATH &&
  location.pathname !== LOGOUT_SUCCESS_CONTEXT_PATH
) {
  checkLoginElseRedirect();
}

if (
  bootstrapData.user?.isActive ||
  (!bootstrapData.user?.isActive &&
    location.pathname !== LOGIN_CONTEXT_PATH &&
    location.pathname !== LOGOUT_SUCCESS_CONTEXT_PATH)
) {
  retry_timeer_id = addOnlineHeartBeat(RETRY_INTERVAL);
  document.addEventListener('visibilitychange', () => {
    if (retry_timeer_id) {
      clearInterval(retry_timeer_id);
    }
    // we only care about the tab becoming visible, not vice versa
    if (document.visibilityState !== 'visible') return;

    retry_timeer_id = addOnlineHeartBeat(RETRY_INTERVAL);

    checkLoginElseRedirect();
  });
}
